import React, { useRef, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import NavBar from '@/components/NavBar';
import Footer from '@/components/MainFooter';
import { Link } from 'react-router-dom';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { optimizedImage } from '@/utils/imageOptimizer';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarImage } from '@/components/ui/avatar';

const SectionWrapper: React.FC<{ id?: string; children: React.ReactNode; className?: string }> = ({ id, children, className }) => (
  <section id={id} className={`w-full py-16 md:py-24 ${className || ''}`}>
    <div className="container mx-auto px-4 md:px-8">
      {children}
    </div>
  </section>
);

const Hero: React.FC = () => {
  return (
    <SectionWrapper>
      <div className="grid md:grid-cols-2 gap-10 items-center">
        <div className="max-w-3xl">
          <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight text-brand-darkgrey">
            Your hiring. Your way.
          </h1>
          <h2 className="mt-4 text-2xl md:text-3xl font-semibold text-brand-blue">
            Finally, a recruiter who doesn’t get lost in keywords.
          </h2>
          <p className="mt-6 text-lg md:text-xl text-brand-grey">
            Candidate Finder understands the role and your personal hiring preferences — and surfaces candidates worth getting on your calendar.
          </p>
          <p className="mt-3 text-sm md:text-base text-muted-foreground">
            Relevance over volume. Control over automation.
          </p>
          <div className="mt-8 flex justify-start">
            <a
              href="https://cutshort.io/a/generic-emp-form"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-brand-blue text-white px-6 py-3 rounded-lg shadow-md transition-all hover:shadow-lg hover:translate-y-[-1px] text-center"
            >
              Get started
            </a>
          </div>
        </div>
        <div className="hidden md:block">
          <img
            src="/lovable-uploads/founders-hero.png"
            alt="From hiring chaos to curated, relevant candidates"
            className="w-full rounded-xl shadow-md border border-gray-100"
            loading="lazy"
            decoding="async"
            sizes="(min-width: 768px) 42rem, 100vw"
          />
        </div>
      </div>
    </SectionWrapper>
  );
};

const Reframing: React.FC = () => {
  return (
    <SectionWrapper className="bg-gray-50">
      <div className="max-w-3xl mx-auto text-center">
        <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight text-brand-darkgrey">
          Why recruiting is broken for founders
        </h1>
        <h2 className="mt-6 text-xl md:text-2xl font-semibold text-brand-grey">
          There are thousands of candidates out there. But very few who match your "type".
        </h2>
        <p className="mt-4 text-lg text-brand-grey">
          You want to hire but the effort is just too high. You try to outsource or delegate but it kills both relevance and speed.
        </p>
        <p className="mt-4 text-lg text-brand-grey">
          We have been in your shoes. And now are fixing it.
        </p>
      </div>
    </SectionWrapper>
  );
};

const HowItWorks: React.FC = () => {
  const steps = [
    {
      title: 'Describe your rules and preferences',
      description:
        'Skills are table stakes. Add what actually matters to you — colleges, prior companies, startup exposure, work setup, ambition, and patterns you care about.'
    },
    {
      title: 'Candidate Finder works 24×7 in the background',
      description:
        'It continuously searches and evaluates profiles against your preferences — without needing your attention.'
    },
    {
      title: 'You say yes — it sets up the conversation',
      description:
        'Approve a candidate, and Candidate Finder will introduce you with your calendar link. You will have complete control from there.'
    }
  ];

  return (
    <SectionWrapper id="how-it-works">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-3xl md:text-4xl font-bold text-brand-darkgrey text-center">
          How Candidate Finder works
        </h2>
        <div className="mt-10 grid md:grid-cols-3 gap-8 md:gap-10">
          {steps.map((step, idx) => (
            <div key={idx} className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
              <h3 className="text-sm font-semibold text-brand-blue">Step {idx + 1}</h3>
              <h4 className="mt-2 text-xl font-semibold text-brand-darkgrey">{step.title}</h4>
              <p className="mt-3 text-brand-grey">{step.description}</p>
            </div>
          ))}
        </div>
      </div>
    </SectionWrapper>
  );
};

const TrustControl: React.FC = () => {
  return (
    <SectionWrapper className="bg-gray-50">
      <div className="max-w-3xl mx-auto text-center">
        <h2 className="text-3xl md:text-4xl font-bold text-brand-darkgrey">
          Not another over-promising AI agent.
        </h2>
        <p className="mt-6 text-lg text-brand-grey">
          Candidate Finder doesn't claim to be a full recruiter. It just focusses on one task that takes the most time - finding the right candidates that match your criteria. And then it waits for your approval before contacting any candidates.
        </p>
        <p className="mt-4 text-lg text-brand-grey">
          And it's supported by our recruiting experts who can help you when you need it.
        </p>
      </div>
    </SectionWrapper>
  );
};

const Pricing: React.FC = () => {
  const chartData = [
    {
      label: 'Candidate Finder',
      value: 30000,
      multiplier: '1x',
      color: 'bg-green-500',
      height: '12%', // Base height for 30k (12% of 250k)
      price: 'INR 29970/ per month',
      description: '(Performance based refunds)'
    },
    {
      label: 'Recruiter + tools',
      value: 125000,
      multiplier: '4x higher',
      color: 'bg-orange-600',
      height: '50%', // Proportional to 1.25L (50% of 250k)
      price: 'INR 1.25 Lakh per month',
      description: '(No guarantees. No flexibilities.)'
    },
    {
      label: 'Agency fees',
      value: 250000,
      multiplier: '8x higher',
      color: 'bg-red-700',
      height: '100%', // Full height for 2.5L (100% of 250k)
      price: 'INR 2.5 Lakhs to 5 Lakhs',
      description: '(Less control. Slow and unpredictable.)'
    }
  ];

  return (
    <SectionWrapper>
      <div className="max-w-5xl mx-auto">
        <h2 className="text-3xl md:text-4xl font-bold text-brand-darkgrey text-center">
          The power of 3, at a fractional cost
        </h2>
        <p className="mt-6 text-lg text-brand-grey text-center">
          Candidate Finder is an expert recruiter + with a sophisticated AI tool kit + unlimited access to the most premium 35 Lakh+ candidate pool in India.
        </p>
        
        {/* Column Chart */}
        <div className="mt-12">
          <div className="flex justify-center gap-6 md:gap-10">
            {chartData.map((item, idx) => (
              <div key={idx} className="flex flex-col items-center flex-1 max-w-[180px]">
                {/* Chart Bar Container */}
                <div className="w-full relative" style={{ height: '250px' }}>
                  {/* Background bar (full height) */}
                  <div className="absolute bottom-0 left-0 right-0 w-full bg-gray-100 rounded-t-lg" style={{ height: '100%' }}></div>
                  {/* Colored bar (proportional height) */}
                  <div
                    className={`absolute bottom-0 left-0 right-0 w-full ${item.color} rounded-t-lg transition-all duration-700 ease-out flex items-center justify-center`}
                    style={{ 
                      height: item.height,
                      minHeight: item.label === 'Candidate Finder' ? '30px' : 'auto',
                      bottom: '0px'
                    }}
                  >
                    <span className="text-white font-bold text-xs md:text-sm">
                      {item.multiplier}
                    </span>
                  </div>
                </div>
                {/* Labels below bars */}
                <div className="mt-4 w-full text-center">
                  <div className="text-sm md:text-base font-semibold text-brand-darkgrey mb-2">
                    {item.label}
                  </div>
                  <div className="text-sm md:text-base font-semibold text-brand-darkgrey mb-1">
                    {item.price}
                  </div>
                  <div className="text-xs md:text-sm text-brand-grey">
                    {item.description}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </SectionWrapper>
  );
};

const FAQ: React.FC = () => {
  const faqItems = [
    {
      question: "What is Candidate Finder and how is it different from traditional recruiting?",
      answer: "Candidate Finder is an AI-powered tool that focuses on one critical task: finding candidates that match your specific hiring preferences. Unlike traditional recruiters or agencies, it doesn't claim to be a full recruiter. It searches 24/7, evaluates candidates against your criteria (colleges, companies, startup exposure, work setup, ambition), and waits for your approval before contacting anyone. You stay in full control."
    },
    {
      question: "How does Candidate Finder work?",
      answer: "Candidate Finder works in three simple steps: (1) You describe your rules and preferences - not just skills, but what actually matters to you like colleges, prior companies, startup exposure, and patterns you care about. (2) Candidate Finder works 24×7 continuously searching and evaluating profiles against your preferences (3) You approve candidates, and Candidate Finder sends personalized intro to both sides with your calendar link. You have complete control from there."
    },
    {
      question: "What happens after I approve a candidate?",
      answer: "Once you approve a candidate, Candidate Finder sends personalized outreach messages to both you and the candidate, then gets the first call booked on your calendar. After that, it stops - you take full control of the conversation and hiring process. Candidate Finder never acts without your explicit approval."
    },
    {
      question: "What kind of candidates I will receive?",
      answer: "First focus is to give you 10+ active + matching candidates from Cutshort, where every 3rd developer in India has signed up. If this fails, Candidate Finder will also find matching candidates from 600M profiles on the internet but it can't guarantee 100% match and job intent."
    },
    {
      question: "What is the pricing?",
      answer: "Candidate Finder is introduced at INR 29,970 per month, which is significantly lower than hiring an in-house recruiter (INR 1L+ per month) or using agencies (INR 2.5-5 Lakhs per hire). There is no limit on number of roles."
    },
    {
      question: "How many candidates I can expect from this? If it doesn't work, do you offer refunds?",
      answer: "Number of candidates depend on your role and criteria. The stricter you are, the fewer candidates you will get. For most roles, you should expect 8-10 active candidates that match your criteria. If you get less, you can request for a refund at hire@cutshort.io"
    },
    {
      question: "How does Candidate Finder compare to hiring an in-house recruiter + buying job postings and database subscriptions?",
      answer: "Hiring a good recruiter + buying tools is a good option when you hiring needs are predictable. Else they become a \"high capex and low effectiveness\" solution. If you are buying job postings and tools for yourself, please add the cost of your time to make the comparison."
    },
    {
      question: "How does Candidate Finder compare to recruitment agencies?",
      answer: "Recruitment agencies charge 8.33% to 15% of CTC per hire with limited relevance, little control over timelines and unpredictable results. Candidate Finder gives you complete control + high speed at 1/8th the cost."
    },
    {
      question: "What kind of candidate pool does Candidate Finder have access to?",
      answer: "Candidate Finder provides unlimited access to the most premium 35 Lakh+ candidate pool in India. This includes verified profiles with deep information about skills, experience, colleges, prior companies, startup exposure, and more. The AI continuously searches and evaluates this pool against your specific preferences."
    },
    {
      question: "What kind of preferences can I set for Candidate Finder?",
      answer: "If you expect a LLM today to reasonably understand a criterion, it should work. For e.g. \"must have done competitive programming or must be from a tier 1 institute\". We natively support requirements around things like education tier, funded startups, product companies, job hopping etc. You can specific each criterion as a must match or a preference. Try to be realistic and not too narrow. W"
    },
    {
      question: "Is Candidate Finder supported by human experts?",
      answer: "Yes, Candidate Finder is supported by our recruiting experts who can help you when you need it. While the AI handles the continuous searching and evaluation, human experts are available to provide guidance, answer questions, and assist with any challenges you might face during the hiring process."
    },
    {
      question: "Can I cancel or get a refund if Candidate Finder doesn't work for me?",
      answer: "Yes, we offer performance-based refunds - up to 100% refund if Candidate Finder doesn't perform as expected. There are no long-term commitments, so you can cancel anytime. We're confident in the value Candidate Finder provides, which is why we offer this guarantee."
    }
  ];

  return (
    <SectionWrapper>
      <div className="max-w-3xl mx-auto">
        <h2 className="text-3xl md:text-4xl font-bold text-brand-darkgrey text-center mb-12">
          Frequently Asked Questions
        </h2>
        
        <Accordion type="single" collapsible className="w-full">
          {faqItems.map((item, index) => (
            <AccordionItem key={index} value={`item-${index}`} className="border-b border-gray-200">
              <AccordionTrigger className="text-lg font-medium text-brand-darkgrey py-5 text-left">
                {item.question}
              </AccordionTrigger>
              <AccordionContent className="text-brand-grey text-base pb-6">
                {item.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </SectionWrapper>
  );
};

const SocialProof: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('opacity-100');
        observer.unobserve(entry.target);
      }
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const successStories = [
    {
      company: "StepSecurity",
      logo: "/lovable-uploads/d6cd13f0-746b-497b-8a48-da1b0b3359c9.png",
      highlight: "Hired first candidate sourced on day 2",
      description: "Saved ₹2 Lakhs in agency fees"
    },
    {
      company: "Averlon",
      logo: "/lovable-uploads/7c7137df-389b-48c9-91c1-10bd139e59d4.png",
      highlight: "Hired from first 5 candidates sourced",
      description: "Saved ₹1.2 Lakhs in agency fees"
    },
    {
      company: "CallHub",
      logo: "/lovable-uploads/caaef211-6b5e-41ee-a6ef-7992a73828c1.png",
      highlight: "Found a hidden candidate due to AI interviews",
      description: "At 40% of expected CTC"
    },
    {
      company: "Fractal",
      logo: "/lovable-uploads/656a10f9-d525-4884-adc1-7910d6b9b235.png",
      highlight: "Made 12 offers in 2 months",
      description: "Staffed a critical client project within timelines"
    }
  ];

  return (
    <SectionWrapper>
      <div ref={sectionRef} className="opacity-0 transition-opacity duration-1000">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-brand-darkgrey mb-6">
            Trusted and loved by 5000+ founders
          </h2>
          <p className="text-lg text-brand-grey">
            They have built their dream teams on Cutshort
          </p>
        </div>
        
        {/* Success Stories Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
          {successStories.map((story, index) => (
            <div key={index} className="bg-white rounded-xl border border-gray-100 shadow-sm p-6 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-center mb-6">
                <img {...optimizedImage(story.logo, `${story.company} logo`, "h-12 object-contain")} />
              </div>
              
              <div className="mb-4">
                <h4 className="text-lg font-semibold text-brand-darkgrey mb-2">{story.highlight}</h4>
                <div className="text-brand-grey italic">
                  {story.description}
                </div>
              </div>
            </div>
          ))}
        </div>
      
        {/* Testimonial Section - Commented out for later */}
        {/*
        <div className="mt-16 max-w-4xl mx-auto">
          <Card className="bg-white border-gray-200 shadow-lg">
            <CardContent className="p-8">
              <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
                <div className="flex-shrink-0">
                  <Avatar className="w-20 h-20">
                    <AvatarImage {...optimizedImage("/lovable-uploads/0234a195-cb13-4934-9053-a178b68e84a0.png", "Ashwini Ganapathy profile photo", "object-cover")} />
                  </Avatar>
                </div>

                <div className="flex-1 text-center md:text-left">
                  <blockquote className="text-lg text-brand-darkgrey mb-4 leading-relaxed">
                    "Cutshort is an unbelievable asset for my team to find quality profiles faster. We have hired 3 high quality engineers and hoping to hire more in coming months with Cutshort."
                  </blockquote>
                  
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                    <div className="mb-4 md:mb-0">
                      <p className="font-semibold text-brand-darkgrey">Ashwini Ganapathy</p>
                      <p className="text-brand-grey">Senior Manager, Talent Acquisition, Gruve.ai</p>
                    </div>
                    
                    <div className="flex justify-center md:justify-end">
                      <img {...optimizedImage("/lovable-uploads/5cfc1c21-8fc9-4c41-a396-ca993c1becb9.png", "Gruve logo", "h-8 object-contain")} />
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        */}
      </div>
    </SectionWrapper>
  );
};

const ClosingCTA: React.FC = () => {
  return (
    <SectionWrapper className="bg-gray-50">
      <div className="max-w-3xl mx-auto text-center">
        <h2 className="text-3xl md:text-4xl font-bold text-brand-darkgrey">
          Founder-quality hiring, without founder-level effort.
        </h2>
        <p className="mt-4 text-lg text-brand-grey">
          Clone the way you hire — while staying fully in control.
        </p>
        <div className="mt-8 flex justify-center">
          <a
            href="https://cutshort.io/a/generic-emp-form"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-brand-blue text-white px-6 py-3 rounded-lg shadow-md transition-all hover:shadow-lg hover:translate-y-[-1px] text-center"
          >
            Get started
          </a>
        </div>
        <div className="mt-6 text-sm text-muted-foreground">
          <p>Finds candidates. Never acts without approval.</p>
          <p>Designed around founder judgment.</p>
          <p>Built for relevance, not volume.</p>
        </div>
      </div>
    </SectionWrapper>
  );
};

const FoundersCandidateFinder: React.FC = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>Candidate Finder for Founders | Cutshort</title>
        <meta
          name="description"
          content="Candidate Finder understands your hiring preferences and surfaces candidates worth getting on your calendar. Relevance over volume. Control over automation."
        />
        <meta property="og:title" content="Candidate Finder for Founders | Cutshort" />
        <meta
          property="og:description"
          content="Relevance over volume. Control over automation. Founder-quality hiring, without founder-level effort."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://cutshort.io/ai-hiring-for-founders" />
        <meta property="og:image" content="/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Candidate Finder for Founders | Cutshort" />
        <meta
          name="twitter:description"
          content="Relevance over volume. Control over automation."
        />
        <meta name="twitter:image" content="/og-image.png" />
        <link rel="canonical" href="https://cutshort.io/ai-hiring-for-founders" />
      </Helmet>

      <NavBar />
      <main>
        <Hero />
        <Reframing />
        <HowItWorks />
        <TrustControl />
        <Pricing />
        <FAQ />
        <SocialProof />
        <ClosingCTA />
      </main>
      <Footer />
    </div>
  );
};

export default FoundersCandidateFinder;


